import { useMemo } from 'react';
import { UseFormReturn, useFieldArray, useWatch } from 'react-hook-form';

import { AnimatePresence } from 'framer-motion';

import { MultiSelect } from 'components/MultiSelect';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { Undef } from 'types/UtilityTypes';

import {
  LTLAccessorial,
  LTLSelectedAccessorial,
  QuickQuoteInputs,
} from '../../types';
import { AccessorialCard } from '../AccessorialCard';
import { CATEGORY_CONFIG, getPrimaryCategory } from '../accessorialUtils';

function getAccessorialId(accessorial: LTLAccessorial): string {
  return (
    accessorial.code ||
    (accessorial.externalId != null ? String(accessorial.externalId) : '')
  );
}

function findAccessorialById(
  accessorials: LTLAccessorial[],
  id: string
): Undef<LTLAccessorial> {
  if (!id) return undefined;
  return accessorials.find(
    (a) =>
      getAccessorialId(a) === id ||
      (a.externalId != null && String(a.externalId) === id)
  );
}

interface AccessorialsSectionProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  accessorials: LTLAccessorial[];
  isLoading?: boolean;
}

/** ThreeG LTL accessorials: flat searchable list (no categories). */
export function AccessorialsSection({
  formMethods,
  accessorials,
  isLoading = false,
}: AccessorialsSectionProps) {
  const { control, watch } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'ltl.selectedAccessorials',
  });

  const selectedAccessorials =
    useWatch({
      control,
      name: 'ltl.selectedAccessorials',
      defaultValue: [],
    }) ?? [];

  const accessorialsOrdered = useMemo(() => {
    const favorites = accessorials.filter((a) => a.isFavorite);
    const nonFavorites = accessorials.filter((a) => !a.isFavorite);
    return favorites.concat(nonFavorites);
  }, [accessorials]);

  const allOptions = useMemo(
    () =>
      accessorialsOrdered.map((accessorial) => ({
        label: accessorial.name,
        value: getAccessorialId(accessorial),
      })),
    [accessorialsOrdered]
  );

  const selectedValues = useMemo(
    () =>
      selectedAccessorials
        .map((s: LTLSelectedAccessorial) =>
          s?.code ? s.code : s?.externalId != null ? String(s.externalId) : ''
        )
        .filter(Boolean) as string[],
    [selectedAccessorials]
  );

  function getSelectedIndex(identifier: string): number {
    return selectedAccessorials.findIndex((a: LTLSelectedAccessorial) => {
      const selectedId =
        a.code || (a.externalId != null ? String(a.externalId) : '');
      return selectedId === identifier;
    });
  }

  function handleChange(nextSelectedValues: string[]) {
    const current = nextSelectedValues;
    const previous = selectedAccessorials
      .map(
        (s: LTLSelectedAccessorial) =>
          s?.code || (s?.externalId != null ? String(s.externalId) : '')
      )
      .filter(Boolean);

    const removed = previous.filter((v) => !current.includes(v));
    const added = current.filter((v) => !previous.includes(v));

    const indicesToRemove = removed
      .map((identifier) =>
        selectedAccessorials.findIndex((a: LTLSelectedAccessorial) => {
          const id =
            a.code || (a.externalId != null ? String(a.externalId) : '');
          return id === identifier;
        })
      )
      .filter((i) => i >= 0)
      .sort((a, b) => b - a);
    indicesToRemove.forEach((index) => remove(index));

    added.forEach((identifier) => {
      const accessorial = findAccessorialById(accessorialsOrdered, identifier);
      if (accessorial) {
        const newSelection: LTLSelectedAccessorial = {
          code: accessorial.code || '',
          name: accessorial.name,
          externalId: accessorial.externalId,
        };

        if (
          accessorial.actions &&
          Object.keys(accessorial.actions).length > 0
        ) {
          newSelection.actionValues = {};
          Object.keys(accessorial.actions).forEach((key) => {
            newSelection.actionValues![key] = '';
          });
        }

        append(newSelection);
      }
    });
  }

  function removeAccessorial(identifier: string) {
    const index = getSelectedIndex(identifier);
    if (index >= 0) {
      remove(index);
    }
  }

  const hasSelectedAccessorials = selectedAccessorials.some(
    (s: LTLSelectedAccessorial) => s?.code || s?.externalId
  );

  return (
    <Flex direction='col' gap='lg' className='w-full'>
      <Typography
        variant='h5'
        className='text-neutral-800 font-semibold whitespace-nowrap'
      >
        Accessorials
      </Typography>

      {isLoading ? (
        <div className='text-sm text-neutral-500 text-center py-4'>
          Loading accessorials...
        </div>
      ) : accessorials.length === 0 ? (
        <div className='text-sm text-neutral-500 text-center py-4'>
          No accessorials available
        </div>
      ) : (
        <Flex direction='col' gap='md' className='w-full'>
          <MultiSelect
            placeholder='Select accessorials...'
            value={selectedValues}
            onChange={handleChange}
            options={allOptions}
            className='w-full'
            displayMode='count'
          />

          {hasSelectedAccessorials && (
            <Flex direction='col' className='w-full'>
              <AnimatePresence initial={false} mode='popLayout'>
                {fields.map((field, index) => {
                  const watched = watch(`ltl.selectedAccessorials.${index}`);

                  let selected = watched;
                  if (!selected) {
                    selected = field as unknown as LTLSelectedAccessorial;
                  }

                  const identifier =
                    selected?.code ||
                    (selected?.externalId != null
                      ? String(selected.externalId)
                      : '');
                  if (!identifier) {
                    return null;
                  }

                  const def = findAccessorialById(
                    accessorialsOrdered,
                    identifier
                  );
                  const category = getPrimaryCategory(def);
                  const categoryConfig = category
                    ? CATEGORY_CONFIG[category]
                    : undefined;

                  return (
                    <AccessorialCard
                      key={field.id}
                      index={index}
                      selected={selected}
                      def={def}
                      categoryConfig={categoryConfig}
                      control={control}
                      onRemove={() => removeAccessorial(identifier)}
                    />
                  );
                })}
              </AnimatePresence>
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  );
}
